/**
 * "Anpassa text" (ADR-0102 phase 1) — the author adapts a beta contract's
 * text over its compiled IR. Every action is appended to the contract's
 * action log; the server replays the log over the current compile and
 * returns the tracked document + a per-entry report, so form edits and
 * text edits co-exist. Conflicts (the compiled text under an edit changed)
 * are listed first for a human to resolve.
 *
 * The document reads clean by default (decision 1); "Visa ändringar mot
 * standard" switches to the track-changes view.
 *
 * Editing runs on the framework's headless adaptation session (ADR-0102
 * addendum): `finetune_get` returns the session inputs, the browser builds
 * `createAdaptationSession` over the log, and its action builders produce
 * the entries the server re-validates on append. Text is edited as token
 * text with form fields as live chips; own sections and own fields
 * ("Egna fält") are log entries like any other change.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { plainText } from '@hyranai/regta-document-ir';
import type {
  ActionResult,
  AdaptationCustomField,
  AdaptationSession,
  BlockRegistry,
  CustomFieldType,
  ResolvedDocumentIR,
} from '@hyranai/regta-document-ir';
import { createRegtaBlockRegistry } from '@hyranai/regta-blocks';
import { ApiError, tenantApi, type FinetuneAction, type FinetuneView } from '../../../api.js';
import { IrEditableDocument, type AdaptApi, type DocumentFocus, type DocumentView } from './IrEditableDocument.js';
import { ReviewSidebar, suggestionKey, type CommentAnchor } from './ReviewSidebar.js';
import { ChangeThreads, createActionComment, useUserNames } from '../../../components/beta-review/ReviewThreads.js';
import {
  assignThreads,
  changeForAddress,
  changeForSeq,
  commentCountsBySeq,
  type ChangeRef,
} from '../../../components/beta-review/review-model.js';
import { useInvalidate, useTenantQuery } from '../../../query.js';
import {
  CUSTOM_FIELD_TYPES,
  CUSTOM_FIELD_TYPE_LABEL,
  buildSession,
  buildWithPending,
  pendingFieldKey,
  refusalText,
  toFinetuneActions,
  usageLabel,
  type PendingField,
} from './adapt.js';
import {
  CONFLICT_REASON_TEXT,
  adaptationCount,
  authorLabel,
  changesLabel,
  commentAnchorSeq,
  entryText,
  findNodeByAddress,
  formatAt,
  groupRows,
  keepAsTextInput,
  logRows,
  revertAllInputs,
  undoActionInput,
  undoTargetFor,
  type GroupRow,
  type LogRow,
} from './model.js';

const ERROR_TEXT: Record<string, string> = {
  finetune_invalid_action: 'Ändringen kunde inte sparas — den är ogiltig för den här delen av dokumentet.',
  finetune_keep_as_text_unresolvable:
    'Stället ändringen gällde finns inte längre, så den kan inte behållas som text. Ta bort ändringen i stället.',
  finetune_requires_ir_contract: 'Texten kan bara anpassas på avtal skapade med den nya dokumentmotorn.',
  contract_not_editable: 'Avtalet kan inte längre ändras.',
  contract_in_review: 'Avtalet ligger ute för granskning – texten kan inte anpassas just nu.',
  locked: 'Texten är lagstadgad och kan inte ändras eller tas bort.',
};

const STALE_NOTICE = 'Dokumentet har ändrats – försök igen. Vyn har laddats om.';
const COMMENT_FAILED_NOTICE = 'Ändringen sparades, men kommentaren kunde inte sparas. Lägg till den med ”Kommentera” vid ändringen.';

const groupKey = (groupId: string): string => `g:${groupId}`;

const STATUS_CLS: Record<LogRow['status'] | GroupRow['status'], string> = {
  applied: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  conflict: 'border-red-200 bg-red-50 text-red-800',
  undone: 'border-stone-200 bg-stone-100 text-stone-500',
  reverted: 'border-stone-200 bg-stone-100 text-stone-500',
  proposed: 'border-amber-200 bg-amber-50 text-amber-800',
  stale: 'border-amber-200 bg-stone-100 text-stone-600',
  rejected: 'border-stone-200 bg-stone-100 text-stone-500',
};

let blockRegistry: BlockRegistry | null = null;
const registry = (): BlockRegistry => (blockRegistry ??= createRegtaBlockRegistry());

const SESSION_ERROR =
  'Dokumentet kunde inte förberedas för redigering i webbläsaren. Ändringarna visas, men texten kan inte anpassas just nu.';

export function AdaptTextPanel({
  contractId,
  attachmentId = null,
  refreshKey,
  actorId,
  allowEdit,
  readOnlyText,
  beforeChange = () => true,
  onChanged,
  mode = 'adapt',
  onValueClick,
}: {
  contractId: string;
  /** Document scope (ADR-0102 addendum 3): null = the main document, otherwise that template bilaga's own log. */
  attachmentId?: string | null;
  /** Changes when server-side inputs change (saved values, parties, status). */
  refreshKey: string;
  actorId: string | null;
  /**
   * The author's editing window (ADR-0102 decision 6: draft, pending); in
   * `review` mode whether new suggestions may be made (in_review).
   */
  allowEdit: boolean;
  /** Why the text is read-only, when it is. */
  readOnlyText?: string | null;
  /** Runs before every change; `false` cancels it (approval-reset confirm). */
  beforeChange?: () => boolean;
  /** After a change was saved (refresh approval / contract state). */
  onChanged?: () => void;
  /**
   * `review` (ADR-0102 addendum 2): the granskning view — the session runs in
   * suggest mode (text edits become suggestions), suggestions are decided
   * here, comments are threaded, and the author's adaptations are violet
   * behind "Visa malländringar". Deciding and commenting follow the
   * contract status in both modes (ADR-0102 addendum 4).
   */
  mode?: 'adapt' | 'review';
  /** Field navigation: a click on a field-bound value. */
  onValueClick?: (key: string, rect: DOMRect) => void;
}) {
  const review = mode === 'review';
  const [showAdaptations, setShowAdaptations] = useState(false);
  const [composer, setComposer] = useState<CommentAnchor | null>(null);
  const invalidate = useInvalidate();
  const threadsQ = useTenantQuery(
    'review_comments.list',
    (cid) => tenantApi.review_comments.list({ contract_id: cid as string }),
    [contractId],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const reviewNames = useUserNames();
  const docThreads = useMemo(
    () => (threadsQ.data?.threads ?? []).filter((t) => t.attachment_id === attachmentId && t.anchor_kind !== 'field'),
    [threadsQ.data, attachmentId],
  );
  const [view, setView] = useState<FinetuneView | null>(null);
  const [issuedDoc, setIssuedDoc] = useState<ResolvedDocumentIR | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [focus, setFocus] = useState<DocumentFocus | null>(null);
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [history, setHistory] = useState(false);
  const [docView, setDocView] = useState<DocumentView>('clean');
  const [highlight, setHighlight] = useState<{ key: string; n: number } | null>(null);
  const loadSeq = useRef(0);
  const asideRef = useRef<HTMLElement | null>(null);

  const load = useCallback(async () => {
    const my = ++loadSeq.current;
    setLoading(true);
    try {
      const v = await tenantApi.contracts.finetune_get({ contractId, track: true, ...(attachmentId ? { attachmentId } : {}) });
      if (loadSeq.current !== my) return;
      setView(v);
      setError(null);
      if (!v.trackedDocument) {
        // Issued: the frozen (already patched) document, read-only.
        const preview = await tenantApi.contracts.ir_preview({ contractId });
        const doc = attachmentId
          ? preview.attachments?.find((a) => a.attachmentId === attachmentId)?.document
          : preview.document;
        if (loadSeq.current === my) setIssuedDoc((doc ?? null) as ResolvedDocumentIR | null);
      } else {
        setIssuedDoc(null);
      }
    } catch (e) {
      if (loadSeq.current !== my) return;
      setError(errorText(e, 'Kunde inte läsa dokumentet.'));
    } finally {
      if (loadSeq.current === my) setLoading(false);
    }
  }, [contractId, attachmentId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    let alive = true;
    tenantApi.users
      .list()
      .then(({ users }) => {
        if (alive) setNames(new Map(users.map((u) => [u.id, u.name ?? u.email])));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const append = useCallback(
    async (actions: FinetuneAction[], comment?: string): Promise<boolean> => {
      if (!view) return false;
      if (actions.some((a) => a.op !== 'rejectSuggestion') && !beforeChange()) return false;
      setBusy(true);
      setNotice(null);
      let saved: { log: FinetuneView['log']; appended: number[] } | null = null;
      try {
        const next = await tenantApi.contracts.finetune_append({
          contractId,
          expectedLastSeq: view.lastSeq,
          actions,
          track: true,
          ...(attachmentId ? { attachmentId } : {}),
        });
        loadSeq.current++;
        setView(next);
        setError(null);
        onChanged?.();
        saved = { log: next.log, appended: next.appended ?? [] };
      } catch (e) {
        if (e instanceof ApiError && (e.status === 409 || e.message === 'finetune_stale_log')) {
          setNotice(STALE_NOTICE);
          await load();
        } else if (e instanceof ApiError && (e.message === 'contract_not_editable' || e.message === 'contract_in_review')) {
          setNotice(ERROR_TEXT[e.message]!);
          onChanged?.();
          await load();
        } else {
          const refusal = e instanceof ApiError ? refusalOf(e) : null;
          const usedBy = e instanceof ApiError && Array.isArray(e.data?.usedBy) ? (e.data.usedBy as number[]) : undefined;
          const text = refusal ? refusalText(refusal, usedBy) : errorText(e, 'Ändringen kunde inte sparas.');
          const batchDecision = actions.length > 1 && actions.every((a) => a.op === 'acceptSuggestion' || a.op === 'rejectSuggestion');
          setNotice(batchDecision ? `${text} Inget av besluten sparades.` : text);
          if (refusal === 'stale' || refusal === 'already_decided') await load();
        }
        return false;
      } finally {
        setBusy(false);
      }
      const body = comment?.trim();
      const seq = body ? commentAnchorSeq(saved.log, saved.appended) : null;
      if (body && seq != null) {
        try {
          await createActionComment(contractId, attachmentId, seq, body);
          await invalidate('review_comments.list', contractId);
        } catch {
          setNotice(COMMENT_FAILED_NOTICE);
        }
      }
      return true;
    },
    [contractId, attachmentId, view, load, beforeChange, onChanged, invalidate],
  );

  // The headless session over the loaded log (live contracts only).
  const built = useMemo((): { session: AdaptationSession | null; error: string | null } => {
    if (!view?.session) return { session: null, error: null };
    try {
      const session = buildSession(view.session, view.log, registry(), actorId ?? 'local', review ? 'suggest' : 'adapt');
      return session ? { session, error: null } : { session: null, error: SESSION_ERROR };
    } catch {
      return { session: null, error: SESSION_ERROR };
    }
  }, [view, actorId, review]);
  const session = built.session;

  const doc = session?.tracked ?? view?.trackedDocument ?? issuedDoc;
  const log = session?.log ?? view?.log ?? null;
  const report = session?.report ?? view?.report ?? [];
  const changeGroups = session?.groups ?? view?.groups ?? [];
  const detachedValues = session?.detachedValues ?? view?.detachedValues ?? [];
  const blockKeys = useMemo(
    () => new Map((doc?.manifest ?? []).map((m) => [m.instanceId, m.blockKey])),
    [doc],
  );
  const editable = allowEdit && !!view?.editable && !!view.trackedDocument && !!session;
  // Anyone who can view may decide suggestions while the contract is live (draft, pending, in_review).
  const decidable = !!view?.editable && !!view.trackedDocument && !!session;
  const commentable = !!threadsQ.data?.writable;
  // In granskning the author's adaptations are read-only; undo is refused server side.
  const adaptEditable = editable && !review;
  const suggestions = session?.suggestions ?? view?.suggestions ?? [];
  const count = adaptationCount(changeGroups);
  const rows = useMemo(() => (log && history ? logRows(log, report, adaptEditable) : []), [log, report, adaptEditable, history]);
  const conflictRows = useMemo(() => (log ? logRows(log, report, false) : []).filter((r) => r.status === 'conflict'), [log, report]);
  const groups = useMemo(() => (log ? groupRows(log, changeGroups, report, adaptEditable) : []), [log, changeGroups, report, adaptEditable]);
  const undoTarget = useMemo(() => {
    const cache = new Map<number, number | null>();
    return (seq: number): number | null => {
      if (!log) return null;
      if (!cache.has(seq)) cache.set(seq, undoTargetFor(log, changeGroups, report, seq));
      return cache.get(seq) ?? null;
    };
  }, [log, changeGroups, report]);

  /** Builds entries with the session (own fields defined first) and appends them. */
  const run = useCallback(
    async (build: (s: AdaptationSession) => ActionResult, pending: readonly PendingField[] = [], comment?: string): Promise<boolean> => {
      if (!session || !view?.session) return false;
      const input = view.session;
      let result: ActionResult;
      try {
        result = buildWithPending(session, pending, (next) => buildSession(input, next, registry(), actorId ?? 'local'), build);
      } catch {
        setNotice('Ändringen kunde inte förberedas.');
        return false;
      }
      if (!result.ok) {
        if (result.reason === 'unchanged') return true;
        setNotice(refusalText(result.reason, result.usedBy));
        return false;
      }
      if (result.entries.length === 0) return true;
      return append(toFinetuneActions(result.entries), comment);
    },
    [session, view, actorId, append],
  );

  const decide = useCallback(
    (seqs: number[], decision: 'accept' | 'reject') => {
      if (!session) return;
      const entries = [];
      for (const seq of seqs) {
        const r = decision === 'accept' ? session.actions.acceptSuggestion(seq) : session.actions.rejectSuggestion(seq);
        if (!r.ok) {
          setNotice(refusalText(r.reason));
          return;
        }
        entries.push(...r.entries);
      }
      if (entries.length > 0) void append(toFinetuneActions(entries));
    },
    [session, append],
  );
  const changes = useMemo((): ChangeRef[] => {
    const ofSuggestion = (s: (typeof suggestions)[number]): ChangeRef => ({
      key: suggestionKey(s.seq),
      seqs: [s.seq],
      address: s.address,
      anchorSeq: s.seq,
    });
    return [
      ...suggestions.filter((s) => s.open).map(ofSuggestion),
      ...groups.map((r) => ({ key: groupKey(r.group.groupId), seqs: r.group.seqs, address: r.address, anchorSeq: r.group.rootSeq })),
      ...suggestions.filter((s) => !s.open).map(ofSuggestion),
    ];
  }, [suggestions, groups]);
  const assigned = useMemo(() => assignThreads(docThreads, changes), [docThreads, changes]);
  const commentCounts = useMemo(() => commentCountsBySeq(changes, assigned.byChange), [changes, assigned]);
  const revealChange = useCallback((key: string) => {
    setHistory(false);
    setHighlight((h) => ({ key, n: (h?.n ?? 0) + 1 }));
  }, []);
  useEffect(() => {
    if (!highlight) return;
    const el = asideRef.current?.querySelector<HTMLElement>(`[data-change-key="${CSS.escape(highlight.key)}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const t = setTimeout(() => setHighlight((h) => (h?.n === highlight.n ? null : h)), 2500);
    return () => clearTimeout(t);
  }, [highlight]);
  const commentOn = (address: string, seq: number | null) => {
    const change = (seq != null ? changeForSeq(changes, seq) : null) ?? changeForAddress(changes, address);
    if (change) {
      setComposer({ kind: 'action', seq: change.anchorSeq, changeKey: change.key });
      revealChange(change.key);
    } else {
      setComposer({ kind: 'node', address });
    }
  };
  const showChangeComments = (seq: number) => {
    const change = changeForSeq(changes, seq);
    if (change) revealChange(change.key);
  };

  const refreshThreads = useCallback(async () => {
    await invalidate('review_comments.list', contractId);
  }, [invalidate, contractId]);

  const adapt = useMemo((): AdaptApi | null => {
    if (!session || !editable || !view?.session) return null;
    const values = view.session.values;
    return {
      session,
      fields: session.availableFields,
      newField: (label: string, type: CustomFieldType, pending: readonly PendingField[]) => {
        const name = label.replace(/\s+/g, ' ').trim();
        const same = (other: string) => other.localeCompare(name, 'sv-SE', { sensitivity: 'accent' }) === 0;
        if (session.customFields.some((f) => same(f.label)) || pending.some((p) => same(p.label))) {
          return 'Det finns redan ett eget fält med det namnet. Välj det i listan eller ge det nya fältet ett annat namn.';
        }
        const key = pendingFieldKey(session, values, name, pending);
        const check = session.actions.defineField({ label: name, type, key });
        return check.ok ? { key, label: name, type } : refusalText(check.reason);
      },
      run,
    };
  }, [session, editable, view, run]);
  const show = (address: string | null) => {
    if (!address) return;
    // A removal is not shown in the clean view: switch to the diff to show it.
    if (docView === 'clean' && doc && findNodeByAddress(doc, address)?.edit?.change === 'removed') setDocView('track');
    setFocus((f) => ({ address, n: (f?.n ?? 0) + 1 }));
  };

  if (loading && !view) {
    return <p className="p-6 text-sm text-stone-500">Laddar dokumentet…</p>;
  }
  if (!view) {
    return (
      <div className="p-6">
        <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">{error ?? 'Kunde inte läsa dokumentet.'}</p>
        <button type="button" className="mt-3 text-sm underline" onClick={() => void load()}>
          Försök igen
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 bg-white px-4 py-2">
        {view.deviationLabel && (
          <span
            className="inline-flex items-center rounded border border-orange-300 bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium text-orange-900"
            title="Texten i ett juridiskt granskat block har ändrats"
          >
            {view.deviationLabel}
          </span>
        )}
        {!editable && (
          <span className="rounded border border-stone-300 bg-stone-50 px-1.5 py-0.5 text-[11px] text-stone-600">Skrivskyddad</span>
        )}
        {view.trackedDocument && review && (
          <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-xs text-stone-700 select-none">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-violet-700"
              checked={showAdaptations}
              onChange={(e) => setShowAdaptations(e.target.checked)}
            />
            Visa malländringar
          </label>
        )}
        {view.trackedDocument && !review && (
          <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-xs text-stone-700 select-none">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-stone-900"
              checked={docView === 'track'}
              onChange={(e) => setDocView(e.target.checked ? 'track' : 'clean')}
            />
            Visa ändringar mot standard
          </label>
        )}
        <span className={(view.trackedDocument ? '' : 'ml-auto ') + 'text-xs text-stone-500'} aria-live="polite">
          {busy ? 'Sparar…' : loading ? 'Uppdaterar…' : count > 0 ? changesLabel(count) : 'Inga ändringar'}
        </span>
      </div>
      <p className="border-b border-stone-200 bg-stone-50 px-4 py-1.5 text-xs text-stone-600">
        {review
          ? editable
            ? 'Ändringar du gör i texten blir förslag (rött och grönt). Godta eller avvisa förslagen i listan. Klicka på ett gult värde för att kommentera uppgiften eller ändra den i formuläret.'
            : (readOnlyText ??
              (decidable
                ? 'Nya förslag kan bara göras medan avtalet granskas. Öppna förslag kan godtas eller avvisas.'
                : 'Avtalet kan inte längre ändras.'))
          : editable
          ? 'Håll muspekaren över en del av texten för att ändra, ta bort eller lägga till – även egna avsnitt med egna fält. Gula fält är kopplade till formuläret och följer med när värdet ändras. Anpassade stycken markeras med en tunn linje i marginalen. Ändras standardtexten under en anpassning blir den en konflikt som måste lösas innan avtalet kan signeras.'
          : (readOnlyText ??
            (view.trackedDocument
              ? 'Texten kan inte anpassas i avtalets nuvarande status.'
              : 'Visar det utfärdade dokumentet. Listan visar hur texten anpassades.'))}
      </p>
      {notice && (
        <div className="flex items-start gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900" role="status">
          <span className="flex-1">{notice}</span>
          <button type="button" className="underline" onClick={() => setNotice(null)}>
            Stäng
          </button>
        </div>
      )}
      {error && <p className="border-b border-red-200 bg-red-50 px-4 py-1.5 text-xs text-red-800">{error}</p>}
      {built.error && allowEdit && (
        <p className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-900">{built.error}</p>
      )}

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="min-h-[50vh] min-w-0 flex-1 overflow-auto bg-stone-100 lg:min-h-0">
          {doc ? (
            <IrEditableDocument
              document={doc}
              view={review ? 'track' : docView}
              editable={editable}
              busy={busy}
              blockKeys={blockKeys}
              focus={focus}
              undoTarget={review ? () => null : undoTarget}
              onActions={append}
              adapt={adapt}
              hideAdaptations={review && !showAdaptations}
              onValueClick={onValueClick}
              onCommentNode={commentable ? commentOn : undefined}
              changeCommentLabel={commentable ? (review ? 'Motivering (valfritt)' : 'Kommentar till ändringen (valfritt)') : null}
              commentCounts={commentCounts}
              onShowChangeComments={showChangeComments}
            />
          ) : (
            <p className="p-6 text-sm text-stone-500">Dokumentet kunde inte visas.</p>
          )}
        </div>

        <aside ref={asideRef} className="flex w-full shrink-0 flex-col gap-4 overflow-auto border-t border-stone-200 bg-white p-4 text-xs lg:w-96 lg:border-t-0 lg:border-l">
          {(review ||
            suggestions.length > 0 ||
            assigned.nodes.length + assigned.actions.length + assigned.document.length > 0 ||
            (composer && composer.kind !== 'action')) && (
            <div className={review ? 'contents' : 'flex flex-col gap-4 rounded border border-stone-200 bg-stone-50/60 p-2'}>
              {!review && (
                <h3 className="text-[11px] font-semibold tracking-wider text-stone-600 uppercase">Granskningspunkter</h3>
              )}
              <ReviewSidebar
                contractId={contractId}
                attachmentId={attachmentId}
                suggestions={suggestions}
                log={log}
                assigned={assigned}
                highlightKey={highlight?.key ?? null}
                names={reviewNames}
                actorId={actorId}
                decidable={decidable}
                suggesting={review && editable}
                commentable={commentable}
                busy={busy}
                composer={composer}
                onComposer={setComposer}
                onDecide={decide}
                onShow={show}
                onThreadsChanged={refreshThreads}
              />
            </div>
          )}
          {conflictRows.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-semibold tracking-wider text-red-800 uppercase">
                Konflikter ({conflictRows.length})
              </h3>
              <p className="mb-2 text-stone-600">Avtalet kan inte skickas för signering förrän konflikterna är lösta.</p>
              <ul className="flex flex-col gap-2">
                {conflictRows.map((row) => (
                  <ConflictItem
                    key={row.seq}
                    row={row}
                    doc={doc}
                    editable={adaptEditable}
                    busy={busy}
                    onShow={() => show(row.address)}
                    onKeep={() => void append([keepAsTextInput(row.seq)])}
                    onDrop={() => void append([undoActionInput(row.seq, 'drop')])}
                  />
                ))}
              </ul>
            </section>
          )}

          {detachedValues.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-semibold tracking-wider text-stone-500 uppercase">Lösgjorda fält</h3>
              <p className="mb-1 text-stone-600">Värden som blivit fast text och inte längre följer formuläret:</p>
              <ul className="flex flex-col gap-1">
                {detachedValues.map((v, i) => (
                  <li key={`${v.patchId}-${i}`} className="flex items-baseline gap-1">
                    <button type="button" className="text-left underline decoration-dotted" onClick={() => show(v.address)}>
                      ”{v.text ?? 'tomt fält'}”
                    </button>
                    {'reason' in v && v.reason === 'field_removed' && <span className="text-stone-500">(fältet finns inte längre)</span>}
                    <span className="text-stone-400">#{v.patchId}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <div className="mb-2 flex items-baseline gap-2">
              <h3 className="text-[11px] font-semibold tracking-wider text-stone-500 uppercase">
                {history ? 'Historik' : 'Ändringar'}
              </h3>
              {(log?.entries.length ?? 0) > 0 && (
                <button type="button" className="ml-auto text-[11px] underline" onClick={() => setHistory((h) => !h)}>
                  {history ? 'Visa ändringar' : 'Visa historik'}
                </button>
              )}
            </div>
            {history ? (
              <ol className="flex flex-col gap-1.5">
                {rows.map((row) => (
                  <li key={row.seq} className={'rounded border p-2 ' + (row.status === 'undone' ? 'border-stone-100 opacity-70' : 'border-stone-200')}>
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-mono text-[10px] text-stone-400">#{row.seq}</span>
                      <span className={'min-w-0 flex-1 truncate font-medium text-stone-800' + (row.status === 'undone' ? ' line-through' : '')} title={row.label}>
                        {row.label}
                      </span>
                      <span className={`shrink-0 rounded border px-1 py-px text-[10px] ${STATUS_CLS[row.status]}`}>{row.statusLabel}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-stone-500">
                      <span>{authorLabel(row.entry.author, actorId, names)}</span>
                      <span>{formatAt(row.entry.at)}</span>
                      <span className="ml-auto flex gap-2">
                        {row.address && row.status !== 'undone' && (
                          <button type="button" className="underline" onClick={() => show(row.address)}>
                            Visa
                          </button>
                        )}
                        {row.revert && (
                          <button
                            type="button"
                            className="underline disabled:opacity-50"
                            disabled={busy}
                            onClick={() => void append([undoActionInput(row.revert!.targetSeq)])}
                          >
                            {row.revert.label}
                          </button>
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            ) : groups.length === 0 ? (
              <p className="text-stone-500">
                {adaptEditable ? 'Inga ändringar ännu. Texten följer standardmallen.' : 'Inga ändringar.'}
              </p>
            ) : (
              <ol className="flex flex-col gap-1.5">
                {groups.map((row) => {
                  const key = groupKey(row.group.groupId);
                  const composing =
                    composer?.kind === 'action' && (composer.changeKey == null ? row.group.seqs.includes(composer.seq) : composer.changeKey === key)
                      ? composer.seq
                      : null;
                  return (
                    <li
                      key={row.group.groupId}
                      data-change-key={key}
                      className={
                        'rounded border p-2 ' +
                        (highlight?.key === key
                          ? 'border-violet-400 ring-2 ring-violet-200'
                          : row.status === 'reverted'
                            ? 'border-stone-100 opacity-70'
                            : 'border-stone-200')
                      }
                    >
                      <div className="flex items-baseline gap-1.5">
                        <span
                          className={'min-w-0 flex-1 truncate font-medium text-stone-800' + (row.status === 'reverted' ? ' line-through' : '')}
                          title={row.label}
                        >
                          {row.label}
                        </span>
                        {row.versionsLabel && <span className="shrink-0 text-[10px] text-stone-500">{row.versionsLabel}</span>}
                        <span className={`shrink-0 rounded border px-1 py-px text-[10px] ${STATUS_CLS[row.status]}`}>{row.statusLabel}</span>
                      </div>
                      {row.carriedOverNote && <p className="mt-0.5 text-[11px] text-stone-500">{row.carriedOverNote}</p>}
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-500">
                        <span>{authorLabel(row.latest.author, actorId, names)}</span>
                        <span>{formatAt(row.latest.at)}</span>
                        <span className="ml-auto flex flex-wrap gap-2">
                          {row.address && (
                            <button type="button" className="underline" onClick={() => show(row.address)}>
                              Visa
                            </button>
                          )}
                          {row.undoHead && (
                            <button
                              type="button"
                              className="underline disabled:opacity-50"
                              disabled={busy}
                              onClick={() => void append([undoActionInput(row.undoHead!.targetSeq, row.undoHead!.reason)])}
                            >
                              {row.undoHead.label}
                            </button>
                          )}
                          {row.revertAll && (
                            <button
                              type="button"
                              className="underline disabled:opacity-50"
                              disabled={busy}
                              title="Ångra alla versioner av den här ändringen"
                              onClick={() => void append(revertAllInputs(log!, row.revertAll!))}
                            >
                              Återställ original
                            </button>
                          )}
                          {row.redoSeq != null && (
                            <button
                              type="button"
                              className="underline disabled:opacity-50"
                              disabled={busy}
                              onClick={() => void append([undoActionInput(row.redoSeq!)])}
                            >
                              Gör om
                            </button>
                          )}
                          {commentable && composing == null && (
                            <button
                              type="button"
                              className="underline"
                              onClick={() => setComposer({ kind: 'action', seq: row.group.rootSeq, changeKey: key })}
                            >
                              Kommentera
                            </button>
                          )}
                        </span>
                      </div>
                      <ChangeThreads
                        contractId={contractId}
                        attachmentId={attachmentId}
                        threads={assigned.byChange.get(key) ?? []}
                        names={reviewNames}
                        writable={commentable}
                        composeSeq={composing}
                        onCloseComposer={() => setComposer(null)}
                        onShowNode={show}
                        onChanged={refreshThreads}
                      />
                    </li>
                  );
                })}
              </ol>
            )}
          </section>

          {!history && !review && (session?.customFields.length || adapt) ? (
            <CustomFieldsSection
              contractId={contractId}
              fields={session?.customFields ?? []}
              session={session}
              editable={!!adapt}
              busy={busy}
              run={run}
              onShow={(seq) => show(changeGroups.find((g) => g.seqs.includes(seq))?.address ?? null)}
            />
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function ConflictItem({
  row,
  doc,
  editable,
  busy,
  onShow,
  onKeep,
  onDrop,
}: {
  row: LogRow;
  doc: ResolvedDocumentIR | null;
  editable: boolean;
  busy: boolean;
  onShow: () => void;
  onKeep: () => void;
  onDrop: () => void;
}) {
  const [open, setOpen] = useState(false);
  const reason = row.report?.reason;
  const mine = entryText(row.entry);
  const current = open && doc && row.address ? findNodeByAddress(doc, row.address) : null;
  return (
    <li className="rounded border border-red-200 bg-red-50/50 p-2">
      <div className="font-medium text-stone-800">
        #{row.seq} {row.label}
      </div>
      {reason && <p className="mt-0.5 text-stone-600">{CONFLICT_REASON_TEXT[reason]}</p>}
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
        {editable && reason === 'fingerprint_mismatch' && (
          <button type="button" className="underline disabled:opacity-50" disabled={busy} onClick={onKeep}>
            Behåll som text
          </button>
        )}
        {editable && (
          <button type="button" className="text-red-800 underline disabled:opacity-50" disabled={busy} onClick={onDrop}>
            Ta bort ändringen
          </button>
        )}
        <button
          type="button"
          className="underline"
          onClick={() => {
            setOpen((o) => !o);
            onShow();
          }}
        >
          {open ? 'Dölj original' : 'Visa original'}
        </button>
      </div>
      {open && (
        <div className="mt-2 flex flex-col gap-1.5">
          <div>
            <div className="text-[10px] font-semibold tracking-wider text-stone-500 uppercase">Dokumentet nu</div>
            <p className="whitespace-pre-wrap text-stone-700">
              {current ? plainText([current]) || '(tomt)' : 'Stället finns inte längre i dokumentet.'}
            </p>
          </div>
          {mine != null && (
            <div>
              <div className="text-[10px] font-semibold tracking-wider text-stone-500 uppercase">Din ändring</div>
              <p className="whitespace-pre-wrap text-stone-700">{mine}</p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function refusalOf(e: ApiError): string | null {
  const refusal = e.data?.refusal;
  return typeof refusal === 'string' ? refusal : null;
}

function errorText(e: unknown, fallback: string): string {
  const message = e instanceof Error ? e.message : '';
  return ERROR_TEXT[message] ?? fallback;
}

/**
 * "Egna fält" (ADR-0102 addendum): the own fields in effect, with where
 * they are used and their value; rename, remove (only when no text uses
 * them) and define without text. Values are filled in the form under
 * "Egna uppgifter".
 */
function CustomFieldsSection({
  contractId,
  fields,
  session,
  editable,
  busy,
  run,
  onShow,
}: {
  contractId: string;
  fields: readonly AdaptationCustomField[];
  session: AdaptationSession | null;
  editable: boolean;
  busy: boolean;
  run: (build: (s: AdaptationSession) => ActionResult) => Promise<boolean>;
  onShow: (seq: number) => void;
}) {
  const [renaming, setRenaming] = useState<{ key: string; label: string } | null>(null);
  const [adding, setAdding] = useState<{ label: string; type: CustomFieldType } | null>(null);
  const preview = (key: string) => session?.availableFields.find((f) => f.key === key)?.preview ?? null;

  const rename = async () => {
    if (!renaming) return;
    const label = renaming.label.replace(/\s+/g, ' ').trim();
    const current = fields.find((f) => f.key === renaming.key);
    if (!label || label === current?.label) {
      setRenaming(null);
      return;
    }
    if (await run((s) => s.actions.updateField(renaming.key, { label }))) setRenaming(null);
  };
  const add = async () => {
    if (!adding) return;
    const label = adding.label.replace(/\s+/g, ' ').trim();
    if (!label) return;
    if (await run((s) => s.actions.defineField({ label, type: adding.type }))) setAdding(null);
  };
  const remove = (f: AdaptationCustomField) => {
    if (!confirm(`Ta bort fältet ”${f.label}”? Ett ifyllt värde används inte längre. Du kan ångra under Visa historik.`)) return;
    void run((s) => s.actions.removeField(f.key));
  };

  return (
    <section>
      <div className="mb-2 flex items-baseline gap-2">
        <h3 className="text-[11px] font-semibold tracking-wider text-stone-500 uppercase">Egna fält</h3>
        {editable && !adding && (
          <button type="button" className="ml-auto text-[11px] underline disabled:opacity-50" disabled={busy} onClick={() => setAdding({ label: '', type: 'text' })}>
            + Nytt fält
          </button>
        )}
      </div>
      {fields.length === 0 && !adding && (
        <p className="text-stone-500">
          Inga egna fält. Skapa ett med ”Infoga fält” när du skriver text, eller med ”+ Nytt fält”.
        </p>
      )}
      <ol className="flex flex-col gap-1.5">
        {fields.map((f) => {
          const value = preview(f.key);
          return (
            <li key={f.key} className="rounded border border-stone-200 p-2">
              {renaming?.key === f.key ? (
                <form
                  className="flex items-center gap-1.5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void rename();
                  }}
                >
                  <input
                    autoFocus
                    aria-label="Fältets namn"
                    className="min-w-0 flex-1 rounded border border-stone-300 px-1.5 py-0.5"
                    value={renaming.label}
                    onChange={(e) => setRenaming({ key: f.key, label: e.target.value })}
                    onKeyDown={(e) => e.key === 'Escape' && setRenaming(null)}
                  />
                  <button type="submit" className="underline disabled:opacity-50" disabled={busy}>
                    Spara
                  </button>
                  <button type="button" className="underline" onClick={() => setRenaming(null)}>
                    Avbryt
                  </button>
                </form>
              ) : (
                <div className="flex items-baseline gap-1.5">
                  <span className="min-w-0 flex-1 truncate font-medium text-stone-800" title={f.help ?? f.label}>
                    {f.label}
                  </span>
                  <span className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1 py-px text-[10px] text-amber-900">
                    {CUSTOM_FIELD_TYPE_LABEL[f.type]}
                  </span>
                </div>
              )}
              <div className="mt-0.5 text-[11px] text-stone-500">
                {value != null ? <>Värde: {value}</> : 'Inte ifyllt'} · {usageLabel(f.usedBy)}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[11px]">
                {f.usedBy.length > 0 && (
                  <button type="button" className="underline" onClick={() => onShow(f.usedBy[0]!)}>
                    Visa
                  </button>
                )}
                <Link to={`/contracts/${contractId}/beta?tab=avtal`} className="underline">
                  Fyll i
                </Link>
                {editable && renaming?.key !== f.key && (
                  <button
                    type="button"
                    className="underline disabled:opacity-50"
                    disabled={busy}
                    onClick={() => setRenaming({ key: f.key, label: f.label })}
                  >
                    Byt namn
                  </button>
                )}
                {editable && (
                  <button
                    type="button"
                    className="text-red-800 underline disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={busy || !f.removable}
                    title={f.removable ? 'Ta bort fältet' : 'Fältet används i texten. Ta bort eller ändra texten som använder det först.'}
                    onClick={() => remove(f)}
                  >
                    Ta bort
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {adding && (
        <form
          className="mt-2 flex flex-col gap-1.5 rounded border border-stone-200 p-2"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <input
            autoFocus
            aria-label="Fältets namn"
            placeholder="Namn, t.ex. Besiktningsdatum"
            className="rounded border border-stone-300 px-1.5 py-0.5"
            value={adding.label}
            onChange={(e) => setAdding({ ...adding, label: e.target.value })}
            onKeyDown={(e) => e.key === 'Escape' && setAdding(null)}
          />
          <div className="flex items-center gap-1.5">
            <select
              aria-label="Typ"
              className="rounded border border-stone-300 px-1 py-0.5"
              value={adding.type}
              onChange={(e) => setAdding({ ...adding, type: e.target.value as CustomFieldType })}
            >
              {CUSTOM_FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CUSTOM_FIELD_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
            <span className="ml-auto flex gap-2">
              <button type="button" className="underline" onClick={() => setAdding(null)}>
                Avbryt
              </button>
              <button type="submit" className="font-medium underline disabled:opacity-50" disabled={busy || !adding.label.trim()}>
                Skapa
              </button>
            </span>
          </div>
          <p className="text-[11px] text-stone-500">Fältet fylls i under ”Egna uppgifter” i formuläret och kan infogas i texten.</p>
        </form>
      )}
    </section>
  );
}
